<template>
    <div class="student-home">
        <!-- PAUSED ACCOUNT -->
        <div v-if="isPausedAccount" class="paused-block">
            <div class="paused-name">{{ helloName }}</div>
            <div class="paused-card">
                <v-icon size="48" color="error" class="mb-2">mdi-alert-circle-outline</v-icon>
                <div>{{ $t('common.account_paused') }}</div>
            </div>
            <current-coach no-title />
        </div>

        <!-- CONTENT -->
        <div v-else class="content">
            <pending-invitations />

            <!-- GREETING -->
            <div class="greeting-block">
                <div class="greeting-date">{{ dateLabel }}</div>
                <div class="greeting-hi">
                    <i18n path="common.hello" tag="span">
                        <template #name><span class="greeting-name">{{ firstName }}</span></template>
                    </i18n>
                    <span> 👋</span>
                </div>
                <div class="greeting-sub">{{ greetingSub }}</div>
            </div>

            <quota-banner :client="businessClient" />

            <skeleton-loader v-if="loading" type="student-home" />

            <template v-else>
                <!-- THIS WEEK -->
                <div class="section-block">
                    <div class="section-title">{{ $t('common.home_this_week') }}</div>
                    <div class="week-card">
                        <home-week-dots :week="thisWeek" />
                        <div class="week-progress">
                            <div class="week-progress-label">
                                <span>{{ $t('common.home_weekly_progress') }}</span>
                                <strong>{{ $t('common.home_sessions_progress', { done: doneThisWeek, total: totalThisWeek }) }}</strong>
                            </div>
                            <div class="progress-bar">
                                <div class="progress-fill" :style="{ width: progressPct + '%' }" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- WEEK NUMBERS -->
                <div class="section-block">
                    <div class="section-title">{{ $t('common.home_week_numbers') }}</div>
                    <div class="stats-row">
                        <home-stat-card :value="doneThisWeek" :label="$t('common.sessions')" value-class="green-color"
                            :delta="$t('common.vs_previous_week')" :delta-state="sessionsDeltaState" />
                        <home-stat-card :value="minutesWeek" :label="$tc('common.minutes', 2)" value-class="teal-color"
                            :delta="minutesDelta" :delta-state="minutesDeltaState" />
                        <home-stat-card :value="`🔥 ${streakValue}`" :label="$t('common.streak_days')"
                            value-class="gradient" :delta="streakDelta" delta-state="neutral" />
                    </div>
                </div>

                <!-- GROUP CLASSES -->
                <div v-if="businessCompanyId" class="section-block">
                    <div class="section-title">{{ $t('business.group_classes') }}</div>
                    <book-shift-card :company-id="businessCompanyId" :student-id="studentId" />
                </div>

                <!-- TODAY TRAINING -->
                <div class="section-block">
                    <div class="section-title">{{ $t('common.home_today_training') }}</div>
                    <today-workout-card :routine="todayRoutine" @open="openRoutine" />
                </div>
            </template>

            <!-- COACH VIDEO -->
            <div v-show="hasCoachVideo" class="section-block">
                <div class="section-title">{{ $t('common.home_from_coach') }}</div>
                <coach-video-card :coach-id="coachId" @has-links="hasCoachVideo = $event" />
            </div>
        </div>
    </div>
</template>

<script>
import PendingInvitations from "@/components/student/PendingInvitations";
import CurrentCoach from "@/components/student/CurrentCoach";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import HomeWeekDots from "@/components/student/home/HomeWeekDots";
import HomeStatCard from "@/components/student/home/HomeStatCard";
import TodayWorkoutCard from "@/components/student/home/TodayWorkoutCard";
import CoachVideoCard from "@/components/student/home/CoachVideoCard";
import QuotaBanner from "@/components/student/home/QuotaBanner";
import BookShiftCard from "@/components/student/home/BookShiftCard";
import { buildWeekFromRoutines, computeAndPersistStreak, fetchAssignedRoutinesRange, studentWeekStart } from "@/utils/Routine";
import { loadClientByEmail, loadCoachCompanyId } from "@/utils/Business";

export default {
    components: {
        PendingInvitations,
        CurrentCoach,
        SkeletonLoader,
        HomeWeekDots,
        HomeStatCard,
        TodayWorkoutCard,
        CoachVideoCard,
        QuotaBanner,
        BookShiftCard,
    },
    data() {
        return {
            loading: true,
            thisWeek: [],
            prevWeek: [],
            todayRoutine: null,
            streakValue: 0,
            recordValue: 0,
            hasCoachVideo: false,
            businessCompanyId: null,
            businessClient: null,
        }
    },
    computed: {
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        },
        firstName(vm = this) {
            return (vm.account?.name || '').trim().split(' ')[0]
        },
        helloName(vm = this) {
            return vm.$t('common.hello', { name: vm.account?.name || '' })
        },
        isPausedAccount(vm = this) {
            return vm.account?.studentSubscription?.paused === true
        },
        dateLabel(vm = this) {
            const format = vm.$i18n.locale === 'en' ? 'dddd, MMMM D, YYYY' : 'dddd, D [de] MMMM [de] YYYY'
            const date = vm.$moment().format(format)
            return date.charAt(0).toUpperCase() + date.slice(1)
        },
        doneThisWeek(vm = this) {
            return vm.countDone(vm.thisWeek)
        },
        donePrevWeek(vm = this) {
            return vm.countDone(vm.prevWeek)
        },
        totalThisWeek(vm = this) {
            return vm.thisWeek.filter(d => d.status !== 'none').length
        },
        progressPct(vm = this) {
            return vm.totalThisWeek ? Math.round(vm.doneThisWeek / vm.totalThisWeek * 100) : 0
        },
        sessionsDeltaState(vm = this) {
            if (vm.doneThisWeek > vm.donePrevWeek) return 'up'
            if (vm.doneThisWeek < vm.donePrevWeek) return 'down'
            return 'neutral'
        },
        minutesWeek(vm = this) {
            return vm.account?.counter?.durationWeek || 0
        },
        minutesDiff(vm = this) {
            return vm.minutesWeek - (vm.account?.counter?.durationPreviousWeek || 0)
        },
        minutesDelta(vm = this) {
            if (vm.minutesDiff > 0) return vm.$t('common.minutes_more', { n: vm.minutesDiff })
            if (vm.minutesDiff < 0) return vm.$t('common.minutes_less', { n: -vm.minutesDiff })
            return vm.$t('common.same_as_last_week')
        },
        minutesDeltaState(vm = this) {
            if (vm.minutesDiff > 0) return 'up'
            if (vm.minutesDiff < 0) return 'down'
            return 'neutral'
        },
        streakDelta(vm = this) {
            if (vm.streakValue > 0 && vm.streakValue === vm.recordValue) return vm.$t('common.personal_record')
            return vm.$t('common.record_short', { n: vm.recordValue })
        },
        greetingSub(vm = this) {
            if (vm.loading) return ''
            if (vm.todayRoutine?.advance?.post) return vm.$t('common.greeting_done')
            if (vm.todayRoutine) return vm.$t('common.greeting_today')
            if (vm.streakValue >= 3) return vm.$t('common.greeting_streak')
            return vm.$t('common.greeting_rest')
        },
    },
    async mounted() {
        await this.load()
    },
    methods: {
        countDone(week) {
            return week.filter(d => d.status === 'done' || d.status === 'competence').length
        },
        async load() {
            const vm = this
            vm.streakValue = vm.account?.counter?.streak || 0
            vm.recordValue = vm.account?.counter?.streakRecord || 0
            if (!vm.studentId) { vm.loading = false; return }
            vm.loadBusiness()
            await vm.withLoading(async () => {
                await Promise.all([vm.loadWeeksAndToday(), vm.loadStreak()])
            })
        },
        // Fuera del camino critico: Turnos y el aviso de cuota solo existen si el
        // entrenador del alumno pertenece a una empresa, y el resto del Inicio no
        // tiene que esperarlos.
        async loadBusiness() {
            const vm = this
            try {
                vm.businessCompanyId = await loadCoachCompanyId(vm.coachId)
                if (!vm.businessCompanyId) return
                vm.businessClient = await loadClientByEmail(vm.businessCompanyId, vm.account?.email)
            } catch (error) {
                console.error('[business] loadBusiness student', error?.code, error?.message)
                vm.businessCompanyId = null
                vm.businessClient = null
            }
        },
        async loadWeeksAndToday() {
            const vm = this
            const thisStart = studentWeekStart()
            const prevStart = studentWeekStart(1)
            const start = prevStart.format('YYYY-MM-DD')
            const end = thisStart.clone().add(6, 'days').format('YYYY-MM-DD')

            const byStudent = await fetchAssignedRoutinesRange([vm.studentId], start, end)
            const routines = byStudent[vm.studentId] || []

            vm.thisWeek = buildWeekFromRoutines(routines, thisStart)
            vm.prevWeek = buildWeekFromRoutines(routines, prevStart)

            const today = vm.$moment().format('YYYY-MM-DD')
            vm.todayRoutine = routines.find(r =>
                r.rawDate?.full === today && (r.routineNumber || 1) === 1 && r.type !== 'competence') || null
        },
        async loadStreak() {
            const vm = this
            const { streak, record } = await computeAndPersistStreak(vm.studentId)
            vm.streakValue = streak
            vm.recordValue = record
        },
        openRoutine(routine) {
            const vm = this
            if (!routine) return
            vm.pushRoute({ name: 'student.routine.detail', params: { uid: routine.id } })
        },
    },
}
</script>

<style scoped>
.student-home {
    max-width: 700px;
    margin: 0 auto;
    width: 100%;
}

.content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-bottom: 24px;
}

.section-block {
    display: flex;
    flex-direction: column;
}

.section-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
}

/* Greeting */
.greeting-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.greeting-date {
    font-size: 13px;
    color: var(--v-txtMuted-base);
    font-weight: 500;
}

.greeting-hi {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.greeting-name {
    color: var(--v-accent-base);
}

.greeting-sub {
    font-size: 14px;
    color: var(--v-txtSecondary-base);
    font-weight: 500;
    margin-top: 2px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

@media (max-width: 599px) {
    .greeting-sub {
        font-size: 12px;
    }
}

/* Week card */
.week-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.week-progress {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.week-progress-label {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.week-progress-label strong {
    color: var(--v-txtPrimary-base);
}

.progress-bar {
    height: 6px;
    background: var(--v-surface2-base);
    border-radius: 3px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: var(--brand-gradient);
    border-radius: 3px;
    transition: width 0.3s;
}

/* Stats */
.stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

@media (max-width: 599px) {
    .stats-row {
        gap: 8px;
    }
}

/* Paused */
.paused-block {
    max-width: 460px;
    margin: 0 auto;
    width: 100%;
}

.paused-name {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-accent-base);
    text-align: center;
    text-transform: capitalize;
}

.paused-card {
    text-align: center;
    margin: 12px 0;
    color: var(--v-txtSecondary-base);
}
</style>
